// PostHog client for gist-admin-panel-v2 (GIS-215), configured per
// Gist/documents/analytics-taxonomy.md.
//
// Everything goes through this module. Nothing imports posthog-js directly, so
// the sanitizer, the required `surface`/`appVersion` properties, the host-route
// allowlist and the identify/reset guards cannot be bypassed by a future call
// site.

// ---------------------------------------------------------------------------
// THE STANDARD BUILD, BECAUSE THIS SURFACE RECORDS
// ---------------------------------------------------------------------------
// This shipped on `module.slim.no-external` first, deliberately — that variant
// has no way to obtain a recorder, so replay was impossible rather than merely
// disabled. Session replay was then asked for on this surface (2026-08-01), and
// a slim build cannot provide it.
//
// HOW THAT WAS VERIFIED, because the older note in gist-pwa is now WRONG and
// will mislead the next person who checks. That note says recording requires
// `__defaultExtensionClasses.sessionRecording` to exist. Against 1.409.5 that
// object is `{}` in BOTH builds and is never repopulated at bundle time, so it
// does not discriminate between them and is not a usable test.
//
// The one thing that does is `loadExternalDependency`. rrweb is not bundled at
// all; it is fetched at runtime, and the recorder entry point reads:
//
//   __PosthogExtensions__?.rrweb?.record && __PosthogExtensions__?.initSessionRecording
//     ? startRecording()
//     : __PosthogExtensions__?.loadExternalDependency?.(instance, kind, cb)
//
// On the standard build that loader is assigned, so the CDN fetch happens and
// recording starts in the callback. On `slim.no-external` it is never assigned,
// the optional call short-circuits to `undefined`, and nothing happens — no
// throw, no log. That is the silent failure to know about: a slim build with
// replay "enabled" initializes cleanly, sends events, and records nothing.
//
//   dist/module.js                    loadExternalDependency assigned:  1
//   dist/module.slim.no-external.js   loadExternalDependency assigned:  0
//
// So the structural guarantee is gone and the protection is now explicit.
//
// MASKING REMOVED 2026-08-10 (Johnson's call, same rule as iOS: mask a
// CREDENTIAL, not content). The `data-ph-block` subtrees on Users.js,
// UserDetailsPopup.js and EventForm.js are gone and `maskAllInputs` is off, so
// admin replays now record member names, photos, user-doc fields and operator
// typing verbatim. What remains:
//
//   1. `data-ph-block` on the coupon list (Coupons/index.js) — a live 100%-off
//      code in a replay is redeemable, which makes it a credential. Also on the
//      per-guest "How was it?" list (Events/ReviewGuestList.js): named reviews,
//      safety reports among them (post-event-question-CONTRACT.md).
//   2. Password-type inputs, which rrweb masks regardless of `maskAllInputs`.
//   3. `autocapture: false` and friends, which were free with slim (the code was
//      absent) and are now load-bearing config.
//
// The cost is real: roughly +35 KB gzipped on the entry chunk over slim, plus
// the ~52 KB rrweb recorder as a separate lazy fetch from
// us-assets.i.posthog.com. That fetch needs no CSP work here — unlike gist-pwa,
// this app serves no Content-Security-Policy at all (see firebase.json).
import posthog from "posthog-js/dist/module";
import { sanitizeProperties } from "./sanitize";
import { matchHostRoute } from "./routes";
import { setVariantFallbackReporter } from "../utils/imageVariant";
import { EVENTS } from "./events";

// GIS-182: image-variant fallbacks are reported through this module. The helper
// does not import it itself, so posthog-js (which this repo's jest config does not
// transform) stays out of every component test that renders an image. `capture` is
// a hoisted declaration below and no-ops until init.
setVariantFallbackReporter((properties) => capture(EVENTS.IMAGE_VARIANT_FALLBACK, properties));

const SURFACE = "admin";

const API_KEY = process.env.REACT_APP_POSTHOG_KEY;
const API_HOST = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

// CRA does not inline package.json, so CI should set REACT_APP_VERSION (the git
// SHA is ideal). Falling back to "dev" is deliberate: an unset version in a
// production build is something you want visible in the data, not disguised as
// a plausible-looking number.
const APP_VERSION = process.env.REACT_APP_VERSION || "dev";

let ready = false;
let identifiedId = null;

/**
 * Boot PostHog. Safe to call more than once — React StrictMode double-invokes
 * effects in development.
 */
export function initAnalytics() {
  if (ready) return;
  if (typeof window === "undefined") return;

  try {
    initPostHog();
  } catch (err) {
    // Analytics is never allowed to break the tool it is measuring, and this
    // one is how the team runs events.
    console.warn("[analytics] init failed; continuing without analytics", err);
    ready = false;
  }
}

function initPostHog() {
  if (!API_KEY) {
    // Loud on purpose. `.env.production` is gitignored and lives only on a
    // developer's disk, so a clean CI build ships no key and posthog-js would
    // otherwise no-op in silence — the failure mode where the funnel comes back
    // empty days later and nobody knows when it broke.
    console.warn(
      "[analytics] REACT_APP_POSTHOG_KEY is not set. No events will be captured. " +
        "If this is a deployed build, add the key to the build environment."
    );
    return;
  }

  posthog.init(API_KEY, {
    api_host: API_HOST,

    // Where the PostHog app itself lives, as opposed to where we send data.
    // Required once api_host is our own proxy domain: posthog derives this by
    // rewriting `.i.posthog.com` -> `.posthog.com` on api_host, and that rewrite
    // is a no-op on a custom domain, so the toolbar would be pointed at
    // edge.gistirl.com and never load. Harmless when api_host is the default.
    ui_host: "https://us.posthog.com",

    // admin.gistirl.com shares the registrable domain with gistirl.com and
    // app.gistirl.com. Set explicitly to match the other two surfaces rather
    // than relying on the library default (taxonomy decision 2).
    //
    // Known and accepted: an operator who visits the marketing site lands in the
    // anonymous acquisition pool until they identify here. `$internal_or_test_user`
    // then retro-labels the person, and person-on-events means the events already
    // ingested keep whatever they had — so a handful of staff pageviews sit in
    // the top of the acquisition funnel. That was weighed in decision 2 and is
    // not worth severing cross-surface identity to avoid.
    cross_subdomain_cookie: true,

    // Page views are fired explicitly, and only for host-facing routes. The
    // automatic $pageview would fire once on load for whatever route the
    // operator happened to open — including /issues and /users — and never
    // again, which is both wrong and unfiltered.
    capture_pageview: false,

    // Must be opted in EXPLICITLY: the default is "if_capture_pageview", which
    // reads our manual-pageview setup as off. Without $pageleave, Web Analytics
    // approximates session duration and bounce from the last event's timestamp.
    // Fired by posthog-js internally on unload, so like $identify it carries no
    // surface/appVersion.
    capture_pageleave: true,

    // These four WERE structural — the slim build simply did not contain the
    // code. On the standard build they are config again, and a client-side
    // boolean wins over remote config for all of them, which keeps the decision
    // in this file even though the PostHog UI also has a switch for each.
    //
    // `autocapture` is the one that matters most here, and it STAYS off. It
    // would hoover up input-adjacent DOM text on a surface that renders whole
    // user documents, which is the single highest-risk configuration in the
    // milestone. The other three went on 2026-08-02: none of them reads DOM text
    // — heatmaps send click coordinates, dead clicks send a count and a target,
    // surveys send only what an operator types into the survey itself.
    autocapture: false,
    capture_heatmaps: true,
    capture_dead_clicks: true,
    disable_surveys: false,

    // Error tracking. Sends the exception type, message and stack; the stack is
    // why sanitize.js walks to depth 6 (a frame object sits at depth 4, and the
    // old cap of 4 replaced every frame with "[redacted]").
    capture_exceptions: true,

    // Core Web Vitals, sent as $web_vitals_* properties. Set as an explicit
    // object rather than left to `capture_performance_opt_in` on the project so
    // that turning web vitals on cannot turn network timing off by omission.
    capture_performance: {
      web_vitals: true,
      network_timing: true,
    },

    // Session replay, on as of 2026-08-01. Masking removed 2026-08-10 — see the
    // header. Recordings now carry on-screen member data and operator typing.
    disable_session_recording: false,
    session_recording: {
      // Off by explicit choice, not omission: replays of operators editing
      // events were unwatchable with every field as asterisks. rrweb still
      // masks password-type inputs regardless of this setting.
      maskAllInputs: false,
      // The opt-out lever. Used only by the coupon list — a live coupon code is
      // redeemable, i.e. a credential, not content.
      blockSelector: "[data-ph-block]",
      // `maskTextSelector` is deliberately NOT set. The project holds
      // `[data-ph-mask]`, and posthog resolves each masking field as
      // `client ?? server` (lazy-loaded-session-recorder.ts:579-581), so pinning
      // it here would silently override the project value and take the lever
      // away from the PostHog UI. The library logs a divergence warning for
      // exactly this and that warning is DEBUG-gated, i.e. invisible in prod.
    },

    // Drops the PII blocklist over every outbound bag, and collapses the
    // uid-bearing `/hosts/:vendorID/edit` path out of $current_url.
    sanitize_properties: sanitizeProperties,

    // No anonymous person profiles. Nobody reaches this app without signing in,
    // so an anonymous profile here would only ever be a half-loaded session.
    person_profiles: "identified_only",

    // `advanced_disable_feature_flags: true` used to sit here: feature flags are
    // read on the PWA (decision 10), nothing on this surface consumes one, and
    // skipping the fetch kept a request off every operator's load.
    //
    // It had to go when surveys went on, and the failure it would have caused is
    // an invisible one. Every survey PostHog creates carries an
    // `internal_targeting_flag_key` — the flag that implements "don't show this
    // again once someone has answered or dismissed it" — and survey eligibility
    // resolves it through `featureFlags.isFeatureEnabled()`. With flags disabled
    // that call returns undefined, the survey is ruled ineligible, and it simply
    // never renders. No error, no warning, nothing in the network tab to notice.
    // So surveys here would have looked configured and done nothing at all.
  });

  ready = true;
}

/**
 * Attach the properties the taxonomy requires on every event (doc §3).
 */
function withGlobals(properties) {
  return { ...(properties || {}), surface: SURFACE, appVersion: APP_VERSION };
}

/**
 * Capture an event. No-ops when analytics is not initialized.
 *
 * Never throws: an analytics failure must not break an operator flow.
 */
export function capture(event, properties) {
  if (!ready) return;
  try {
    posthog.capture(event, withGlobals(properties));
  } catch (err) {
    console.warn(`[analytics] capture failed for ${event}`, err);
  }
}

/**
 * Page view for a host-facing route. Silently does nothing on any other route —
 * that is the super-admin exclusion, and it is enforced here rather than at the
 * call site so a second caller cannot forget it.
 *
 * Sends an ABSOLUTE url, matching the PWA and landing: posthog-js auto-attaches
 * `$current_url` from window.location on every other event, and PostHog's
 * sessions table derives the session entry URL from it, so a relative path here
 * would make one page look like two.
 *
 * `route` carries the matched PATTERN (`/events/:eventID/edit`), which is what
 * you group by. `$current_url` keeps the real path for anyone who needs it.
 *
 * @param {string} path pathname from the router
 * @returns {boolean} whether anything was captured — for tests, not callers
 */
export function capturePageview(path) {
  const route = matchHostRoute(path);
  if (!route) return false;
  const url =
    typeof window !== "undefined" && window.location
      ? `${window.location.origin}${path || ""}`
      : path;
  capture("$pageview", { route, ...(url ? { $current_url: url } : {}) });
  return true;
}

/**
 * Identify the signed-in host or operator. Idempotent per uid.
 *
 * Decision 4 is explicit that this surface identifies normally even though it
 * emits no activation events — the person is the same person as in the PWA, and
 * `userID` is the distinct_id on every surface (never the phone).
 *
 * The uid-keyed guard is load-bearing here: `GlobalContext` runs a warm path
 * that sets state from `localStorage` and then a fresh path that sets it again
 * from the server, so this is called at least twice per sign-in.
 *
 * @param {Object} user the gist users doc
 * @param {Object} [vendor] the gist vendors doc, for `isHost`
 */
export function identifyUser(user, vendor) {
  if (!ready) return;
  const userID = user?.userID;
  if (!userID) return;

  try {
    // Person properties are sanitized HERE, not by the library. posthog-js
    // 1.409 runs sanitize_properties over event properties and $set_once, but
    // the $identify `$set` payload is attached raw — so the module-level
    // guarantee only holds if we sanitize this path ourselves.
    const props = sanitizeProperties(personProperties(vendor));
    delete props.$ip; // sanitizeProperties stamps this for events; not a person prop

    if (identifiedId === userID) {
      // Already identified. Still refresh, because the warm path identifies from
      // the cached vendor and the fresh one lands a moment later.
      if (Object.keys(props).length) posthog.setPersonProperties(props);
      return;
    }
    posthog.identify(userID, props);
    identifiedId = userID;
  } catch (err) {
    console.warn("[analytics] identify failed", err);
  }
}

/**
 * Person properties this surface is allowed to write (doc §1).
 *
 * ONE WRITER PER PROPERTY, and person-on-events is enabled, so a second writer
 * freezes wrong values onto every historical event. The split:
 *
 *   isHost      OURS. This is the only client that loads the vendors doc, so
 *               nothing else can answer it. Was unwritten before this shipped.
 *   signupDate  gist-pwa's. Not written here.
 *   isPremium   gist-pwa's. Not written here — and this panel would be actively
 *               wrong about it, since it reads vendor docs, not the member
 *               `subscription` map the entitlement actually comes from.
 *   region      still unwritten anywhere. Not ours to start: the doc defines it
 *               as `nearestRegion(userLocations/{uid}.home)` and this app never
 *               reads userLocations.
 */
function personProperties(vendor) {
  const props = {};

  // The trap the doc calls out by name: `isHost` is NOT vendors-doc existence.
  // `GlobalContext` auto-provisions `vendors/{uid}` for ANY authenticated user
  // (see its `VendorAPI.createVendor` call), so doc-existence would report every
  // super-admin operator and every unapproved applicant as a host.
  // `becameHostVia` is only written by the approval path.
  //
  // Written only when true, never `false` — the same rule as
  // `$internal_or_test_user` below and for the same reason. Absent already means
  // "not established as a host", and an explicit `false` would be frozen onto
  // the history of every member who ever opened this panel. It also keeps us
  // honest about hosts provisioned before `becameHostVia` existed: they read as
  // absent, which is "unknown", rather than as a confident denial.
  if (vendor && vendor.becameHostVia) props.isHost = true;

  return props;
}

/**
 * Update person properties after identify. No-op before identify, so it can
 * never create an anonymous person profile.
 */
export function setPersonProperties(properties) {
  if (!ready || !identifiedId || !properties) return;
  try {
    posthog.setPersonProperties(properties);
  } catch (err) {
    console.warn("[analytics] setPersonProperties failed", err);
  }
}

/**
 * Mark staff so their sessions can be filtered out of every member funnel.
 *
 * This closes the gap decision 9 recorded as known and open: the PWA was the
 * only writer, so staff who use ONLY admin.gistirl.com were never marked.
 *
 * Read from the ID TOKEN CLAIM, never `globalState.isSuperAdmin` — that value
 * is `claim OR vendors/{uid}.isSuperAdmin`, and it is also seeded from
 * `localStorage`, which is user-mutable. GIS-215 requires the claim specifically.
 * The practical difference: a flag-provisioned operator with no claim is NOT
 * marked internal by this. That is the conservative direction (they show up as a
 * normal person rather than being wrongly excluded from member metrics), and it
 * matches what the PWA does for the same person.
 *
 * Set only when true, never `false`, and written as a BOOLEAN. The GIS-212
 * cohort compares against the string `"true"` — that is a property of the cohort
 * rule, not of the value, and the PWA writes a boolean here too. Writing a
 * string to "match" the cohort would diverge the two writers.
 *
 * Async and deliberately unawaited by the caller: `getIdTokenResult()` can hit
 * the network, and nothing about signing in should wait on an analytics label.
 *
 * @param {Object} firebaseUser the Firebase Auth user from the auth listener
 */
export async function markInternalIfStaff(firebaseUser) {
  if (!ready || typeof firebaseUser?.getIdTokenResult !== "function") return;
  try {
    const token = await firebaseUser.getIdTokenResult();
    if (token?.claims?.superAdmin !== true) return;
    setPersonProperties({ $internal_or_test_user: true });
  } catch (err) {
    console.warn("[analytics] internal-user check failed", err);
  }
}

/**
 * Reset on sign-out. Only fires if somebody was actually identified in this
 * context.
 *
 * The guard matters for the same reason it does on the PWA: the signed-out
 * branch of the auth listener also runs on a cold start, and an unconditional
 * reset there would mint a fresh anonymous distinct_id on every page load.
 */
export function resetUser() {
  if (!ready || !identifiedId) return;
  try {
    posthog.reset();
  } catch (err) {
    console.warn("[analytics] reset failed", err);
  } finally {
    identifiedId = null;
  }
}

export { EVENTS } from "./events";
